import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { StatusTone } from "@/lib/statusTone";

/**
 * Pílula de status do redesign 2026. Só sabe pintar um tom; o mapa
 * "status do domínio → rótulo + tom" vem de src/lib/statusTone.ts (parcelas,
 * épicos, sprints) ou de STAGE_META (estágio do lead). Não busca dado, não
 * decide significado.
 */
export interface StatusBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  /** Tom semântico — mapeia para os tokens ok/warn/bad/info/notice/primary/muted. */
  tone?: StatusTone;
  /** `pill` = arredondada (estágio do lead, cabeçalhos); `tag` = cantos 7px (tabelas do Financeiro). */
  shape?: "pill" | "tag";
  /** Bolinha na cor do texto antes do rótulo. */
  dot?: boolean;
  children: ReactNode;
}

const TONE_CLASS: Record<StatusTone, string> = {
  neutral: "bg-muted text-muted-foreground",
  primary: "bg-accent text-primary",
  info: "bg-info-soft text-info",
  ok: "bg-ok-soft text-ok",
  warn: "bg-warn-soft text-warn",
  notice: "bg-notice-soft text-notice",
  bad: "bg-bad-soft text-bad",
};

const SHAPE_CLASS = {
  pill: "h-6 rounded-full px-2.5 text-[11.5px]",
  tag: "h-[23px] rounded-[7px] px-2.5 text-[11px]",
} as const;

export function StatusBadge({
  tone = "neutral",
  shape = "pill",
  dot = false,
  className,
  children,
  ...props
}: StatusBadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap font-bold",
        SHAPE_CLASS[shape],
        TONE_CLASS[tone],
        className,
      )}
      {...props}
    >
      {dot && <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}
